// The app's one sky and its two screens: the sky stage behind everything,
// home in the page's flow, play in a fixed layer above. The router says
// where to be; this host runs the trip there (sceneMachine). Fades are CSS
// transitions; the pan animates the stage and the play layer together with
// the Web Animations API, so it stays on the compositor while the level
// loads, and a change of mind reverses it from where it is. Under reduced
// motion the sky cuts between the halves while the UI is faded out.
import { useEffect, useLayoutEffect, useReducer, useRef, useState, type CSSProperties } from 'react';
import { homePath, matchHoroscopeRoute, navigate, usePath, type HoroscopeRoute, type PlayRoute } from '../../app/router';
import { HOROSCOPE_PAUSED } from '../../systems/horoscope/paused';
import { cancelConfirm, playSession, setMenuOpen } from '../../systems/play/playSession';
import { useStore, useStoreSelect } from '../../systems/store';
import { nightStore } from '../../systems/night/nightStore';
import { HomeScreen } from '../home/HomeScreen';
import { HoroscopeScreen } from '../horoscope/HoroscopeScreen';
import { PlayScreen } from '../play/PlayScreen';
import { ambientSettings, weekdayPreview } from '../sky/ambient/ambientSettings';
import { useReducedMotion } from '../sky/reducedMotion';
import { SkyStage } from '../sky/SkyStage';
import { weekdayFromDate } from '../sky/weekdaySkies';
import { heading, mounted, restingHalf, sceneReducer, uiShown, type Scene, type ScenePhase } from './sceneMachine';
import './scene.css';

const STAGE_AT: Record<Scene, string> = { home: 'translateY(-50%)', play: 'translateY(0)' };
const PLAY_LAYER_AT: Record<Scene, string> = { home: 'translateY(-100%)', play: 'translateY(0)' };

/** The menu tray's slide (menu.css), plus a beat, before the trip home begins. */
const TRAY_CLOSE_MS = 260;

export function SceneHost({ playRoute }: { playRoute: PlayRoute | null }) {
  const target: Scene = playRoute ? 'play' : 'home';
  const [phase, dispatch] = useReducer(sceneReducer, target, (at): ScenePhase => ({ kind: 'rest', at }));
  const t = useStore(ambientSettings).transition;
  const reduced = useReducedMotion();
  const stageRef = useRef<HTMLDivElement>(null);
  const playLayerRef = useRef<HTMLDivElement>(null);
  const pan = useRef<{ anims: Animation[]; to: Scene } | null>(null);

  // The play screen keeps its route while it fades out on the way home.
  const [route, setRoute] = useState(playRoute);
  if (playRoute && (playRoute.levelId !== route?.levelId || playRoute.date !== route?.date)) setRoute(playRoute);

  useEffect(() => {
    // Leaving play with its menu open (Home in the menu, or the back
    // button): the tray slides shut first, then the trip begins. An open
    // confirmation is simply dismissed.
    const s = playSession.get();
    if (target === 'home' && s.confirm) cancelConfirm();
    if (target === 'home' && s.menuOpen) {
      setMenuOpen(false);
      const id = setTimeout(() => dispatch({ type: 'go', to: target }), TRAY_CLOSE_MS);
      return () => clearTimeout(id);
    }
    dispatch({ type: 'go', to: target });
  }, [target]);

  // The fades: wait them out.
  useEffect(() => {
    const ms = phase.kind === 'leave' ? t.fadeOutMs : phase.kind === 'arrive' ? (phase.at === 'home' ? t.homeFadeMs : t.hudFadeMs) : null;
    if (ms === null) return;
    const id = setTimeout(() => dispatch({ type: 'step' }), ms);
    return () => clearTimeout(id);
  }, [phase, t.fadeOutMs, t.homeFadeMs, t.hudFadeMs]);

  // The pan. Layout effects, so starting it and handing over to the resting
  // position both happen before the frame is painted.
  useLayoutEffect(() => {
    if (phase.kind !== 'pan') {
      if (pan.current) {
        for (const a of pan.current.anims) a.cancel();
        pan.current = null;
      }
      return;
    }
    if (reduced) {
      dispatch({ type: 'step' });
      return;
    }
    const running = pan.current;
    if (running && running.to !== phase.to) {
      for (const a of running.anims) a.reverse();
      running.to = phase.to;
    } else if (!running) {
      const timing: KeyframeAnimationOptions = { duration: t.panMs, easing: t.easing, fill: 'both' };
      const anims = [stageRef.current!.animate([{ transform: STAGE_AT[phase.from] }, { transform: STAGE_AT[phase.to] }], timing)];
      if (playLayerRef.current) anims.push(playLayerRef.current.animate([{ transform: PLAY_LAYER_AT[phase.from] }, { transform: PLAY_LAYER_AT[phase.to] }], timing));
      pan.current = { anims, to: phase.to };
    }
    let current = true;
    pan.current!.anims[0]!.onfinish = () => current && dispatch({ type: 'step' });
    return () => {
      current = false;
    };
  }, [phase, reduced, t.panMs, t.easing]);

  // Home shows its night's sky, a past night's as soon as it is asked for.
  const homeDay = useStoreSelect(nightStore, (s) => (s.requested ? weekdayFromDate(s.requested) : (s.night?.weekday ?? null)));
  const playDate = useStoreSelect(playSession, (s) => s.play?.date ?? null) ?? route?.date ?? null;
  const playDay = playDate ? weekdayFromDate(playDate) : null;
  const preview = useStore(weekdayPreview);
  const weekday = preview ?? (heading(phase) === 'play' ? (playDay ?? homeDay) : (homeDay ?? playDay));

  // The Horoscope redraws the home half as a planisphere; it holds still.
  const chart = !HOROSCOPE_PAUSED && matchHoroscopeRoute(usePath()) !== null && target === 'home';

  const homeShown = uiShown(phase, 'home');
  const hudShown = uiShown(phase, 'play');
  const homeFade = `${homeShown ? t.homeFadeMs : t.fadeOutMs}ms`;

  return (
    <>
      <SkyStage
        ref={stageRef}
        weekday={weekday}
        at={restingHalf(phase)}
        half={restingHalf(phase)}
        drifting={homeShown && !chart}
        resting={phase.kind === 'rest' && !chart}
        figuresShown={homeShown}
        figuresFadeMs={homeShown ? t.homeFadeMs : t.fadeOutMs}
        chart={chart}
      />
      {mounted(phase, 'home') && (
        <div className="scene-home" data-shown={homeShown} inert={!homeShown} style={{ transitionDuration: homeFade }}>
          <HomeHalf />
        </div>
      )}
      {mounted(phase, 'play') && route && (
        <div
          ref={playLayerRef}
          className="scene-play"
          data-at={restingHalf(phase)}
          data-hud={hudShown ? 'shown' : 'hidden'}
          inert={!hudShown}
          style={{ '--hud-fade': `${hudShown ? t.hudFadeMs : t.fadeOutMs}ms` } as CSSProperties}
        >
          <PlayScreen key={`${route.levelId}/${route.date ?? 'tonight'}`} route={route} arrived={phase.kind === 'rest' && phase.at === 'play'} />
        </div>
      )}
    </>
  );
}

/** Each screen's half of the trip between home and the Horoscope (Figma's 0 / 300 / 600 ms). */
const SWAP_MS = 300;

/**
 * The home half of the sky holds home, or the night's Horoscope. Between
 * them the screen leaving fades out while the sky turns to or from the
 * chart, then the other fades in over the finished sky.
 */
function HomeHalf() {
  const path = usePath();
  const route = HOROSCOPE_PAUSED ? null : matchHoroscopeRoute(path);
  // While the Horoscope is paused its addresses go home to their night.
  useEffect(() => {
    if (HOROSCOPE_PAUSED && matchHoroscopeRoute(path)) navigate(homePath(), true);
  }, [path]);
  const reduced = useReducedMotion();
  const key = route ? `horoscope/${route.date ?? 'tonight'}` : 'home';
  const date = route?.date ?? null;
  const [shown, setShown] = useState<{ key: string; route: HoroscopeRoute | null }>(() => ({ key, route }));
  const leaving = shown.key !== key;
  useEffect(() => {
    if (!leaving) return;
    const id = setTimeout(() => setShown({ key, route: key === 'home' ? null : { date } }), reduced ? 0 : SWAP_MS);
    return () => clearTimeout(id);
  }, [leaving, key, date, reduced]);
  return (
    <div className="home-half" data-leaving={leaving}>
      {shown.route ? <HoroscopeScreen key={shown.key} route={shown.route} /> : <HomeScreen />}
    </div>
  );
}
